import { useEffect, useState } from "react";
import { Plus, Trash2, CalendarDays, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const blank = { date: "", name: "", type: "public" };
const YEARS = [2026, 2027, 2028];

export default function HolidaysAdmin() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(blank);
  const [impYear, setImpYear] = useState("2026");
  const [importing, setImporting] = useState(false);

  const load = () => api.get("/admin/holidays").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.date) return toast.error("Pick a date");
    try { await api.post("/admin/holidays", form); toast.success("Holiday saved"); setForm(blank); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => { if (!window.confirm("Remove this holiday?")) return; await api.delete(`/admin/holidays/${id}`); load(); };

  const importIndia = async () => {
    setImporting(true);
    try {
      const { data } = await api.post(`/admin/holidays/import?year=${impYear}`);
      if (!data.curated) toast.info(`Only the 3 national holidays are available for ${impYear}. Added ${data.added}.`);
      else toast.success(`Imported India ${impYear} holidays · ${data.added} added, ${data.skipped} already present`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setImporting(false); }
  };

  return (
    <div className="max-w-3xl">
      <h1 className="font-display font-extrabold text-3xl mb-1">Holidays</h1>
      <p className="text-slate-400 mb-6">Mark public & state holidays. On these dates every game uses its <b className="text-[#F9CF04]">Holiday</b> price. Fridays, Saturdays and Sundays automatically use the <b className="text-[#F9CF04]">Weekend</b> price.</p>

      <div className="rounded-2xl border border-white/10 bg-[#241211] p-5 mb-6">
        <div className="grid sm:grid-cols-4 gap-3 items-end">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Date</label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="holiday-date" className="bg-black/30 border-white/10" />
          </div>
          <div className="sm:col-span-1">
            <label className="text-xs text-slate-400 mb-1 block">Name</label>
            <Input placeholder="e.g. Diwali" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="holiday-name" className="bg-black/30 border-white/10" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Type</label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="holiday-type" className="bg-black/30 border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public holiday</SelectItem>
                <SelectItem value="state">State holiday</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={add} data-testid="add-holiday-btn" className="bg-[#F9CF04] hover:bg-[#ffdb2e] text-[#1a0a0a] font-semibold"><Plus className="h-4 w-4 mr-1" /> Add</Button>
        </div>
      </div>

      <div className="rounded-2xl border border-[#F9CF04]/30 bg-gradient-to-br from-[#2a1512] to-[#241211] p-5 mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[12rem]">
            <div className="flex items-center gap-2 mb-1"><Sparkles className="h-4 w-4 text-[#F9CF04]" /><span className="font-semibold">Bulk-add India's public holidays</span></div>
            <p className="text-xs text-slate-400">Adds the year's gazetted national holidays (Diwali, Holi, Eid, Christmas & more) in one click. Existing dates are skipped.</p>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Year</label>
            <Select value={impYear} onValueChange={setImpYear}>
              <SelectTrigger data-testid="import-year" className="w-28 bg-black/30 border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={importIndia} disabled={importing} data-testid="import-india-btn" className="bg-[#F9CF04] hover:bg-[#ffdb2e] text-[#1a0a0a] font-semibold">
            {importing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />} Import {impYear} holidays
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#241211] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-black/30 text-slate-400 text-xs uppercase">
            <tr>{["Date", "Name", "Type", ""].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((h) => (
              <tr key={h.id} className="border-t border-white/5" data-testid={`holiday-${h.id}`}>
                <td className="px-4 py-3 font-mono text-[#F9CF04]">{h.date}</td>
                <td className="px-4 py-3">{h.name || "—"}</td>
                <td className="px-4 py-3"><span className="inline-flex items-center gap-1 text-xs"><CalendarDays className="h-3.5 w-3.5 text-slate-500" /> {h.type === "state" ? "State" : "Public"}</span></td>
                <td className="px-4 py-3 text-right"><button onClick={() => del(h.id)} className="text-red-400 hover:text-red-300" data-testid={`del-holiday-${h.id}`}><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-500">No holidays added yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
